import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Gift, Copy, Share2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { api } from "../lib/api";

const ST = { pending: ["بانتظار أول دفعة", "bg-amber-50 text-amber-700"], rewarded: ["تمت المكافأة", "bg-emerald-50 text-emerald-700"], capped: ["تجاوز الحد السنوي", "bg-slate-100 text-slate-500"] };

export const useReferralAutoClaim = (isManager) => {
  useEffect(() => {
    const code = localStorage.getItem("wz_ref");
    if (!isManager || !code) return;
    localStorage.removeItem("wz_ref");
    api.referralClaim(code).then((r) => toast.success(`تم تسجيل دعوتك من ${r.referrer || "شركة شريكة"}`)).catch(() => {});
  }, [isManager]);
};

const ClaimBox = ({ onDone }) => {
  const [code, setCode] = useState("");
  const claim = async () => {
    try { await api.referralClaim(code); toast.success("تم تسجيل رمز الإحالة"); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر التسجيل"); }
  };
  return (
    <div className="flex gap-2 rounded-xl border border-dashed p-3">
      <Input data-testid="referral-claim-input" placeholder="لديك رمز إحالة؟ أدخله هنا" value={code} onChange={(e) => setCode(e.target.value)} />
      <Button variant="outline" data-testid="referral-claim-btn" disabled={!code.trim()} onClick={claim}>تسجيل</Button>
    </div>
  );
};

export const ReferralDialog = ({ open, onClose }) => {
  const [d, setD] = useState(null);
  const load = () => api.referralMe().then(setD).catch(() => toast.error("تعذّر التحميل"));
  useEffect(() => { if (open) load(); }, [open]);
  const link = d ? `${window.location.origin}/?ref=${d.code}` : "";
  const copy = (t) => navigator.clipboard.writeText(t).then(() => toast.success("تم النسخ"));
  const share = () => (navigator.share ? navigator.share({ title: "WorkZone", text: `جرّب WorkZone لإدارة شركتك — سجّل عبر رابطي:`, url: link }).catch(() => {}) : copy(link));
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent dir="rtl" className="sm:max-w-lg font-body max-h-[85vh] overflow-y-auto" data-testid="referral-dialog">
        <DialogHeader className="text-right">
          <DialogTitle className="flex items-center gap-2 font-heading"><Gift className="h-5 w-5 text-rose-500" />برنامج الإحالة</DialogTitle>
          <DialogDescription>ادعُ شركة أخرى، واحصل على شهر اشتراك مجاني عند أول دفعة لها (حتى {d?.cap ?? 12} شهراً في السنة).</DialogDescription>
        </DialogHeader>
        {d && (
          <div className="space-y-4">
            <div className="rounded-2xl bg-gradient-to-l from-rose-500 to-orange-500 p-5 text-white">
              <p className="text-xs opacity-90">رمز الإحالة الخاص بشركتك</p>
              <p className="mt-1 font-mono text-3xl font-black tracking-widest" data-testid="referral-code">{d.code}</p>
              <p className="mt-2 text-xs opacity-90" data-testid="referral-earned">الشهور المكتسبة هذا العام: {d.earned_this_year} / {d.cap}</p>
            </div>
            <div className="flex gap-2">
              <Input readOnly value={link} dir="ltr" className="text-xs" data-testid="referral-link" />
              <Button size="icon" variant="outline" onClick={() => copy(link)} data-testid="referral-copy"><Copy className="h-4 w-4" /></Button>
              <Button size="icon" onClick={share} data-testid="referral-share"><Share2 className="h-4 w-4" /></Button>
            </div>
            {!d.referred_by && <ClaimBox onDone={load} />}
            <div>
              <p className="mb-2 text-sm font-semibold">الشركات المدعوّة ({d.referrals.length})</p>
              {d.referrals.map((r) => (
                <div key={r.id} className="flex items-center justify-between border-b py-2 text-sm last:border-0" data-testid={`referral-row-${r.id}`}>
                  <span>{r.referred_name}<span className="mr-2 text-xs text-slate-400">{r.created_at?.slice(0, 10)}</span></span>
                  <span className={`rounded-full px-2 py-0.5 text-[11px] ${ST[r.status]?.[1]}`}>{ST[r.status]?.[0]}</span>
                </div>
              ))}
              {!d.referrals.length && <p className="py-3 text-center text-xs text-slate-400">لم تدعُ أي شركة بعد — شارك رابطك الآن</p>}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
